
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ScoreBoard } from "@/components/screen/ScoreBoard";
import { LoadingState } from "@/components/screen/LoadingState";
import { ErrorState } from "@/components/screen/ErrorState";
import { CampaignScreen } from "@/components/screen/CampaignScreen";
import { Tip } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { tipsApi } from "@/services/api/tips";

const CAMPAIGN_DURATION = 20000; // 20 seconds
const LEADERBOARD_DURATION = 30000; // 30 seconds

export default function ScreenDisplay() {
  const [displayMode, setDisplayMode] = useState<'campaign' | 'leaderboard'>('campaign');
  const { toast } = useToast();

  const { 
    data: tips, 
    isLoading, 
    error, 
    refetch 
  } = useQuery({
    queryKey: ['approved-tips'],
    queryFn: tipsApi.fetchApprovedTips,
    retry: 3,
    staleTime: 30000,
  });

  useEffect(() => {
    console.log('Setting up real-time subscription');
    const channel = supabase
      .channel('tips-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tips',
          filter: 'status=eq.approved'
        },
        (payload) => {
          console.log('Real-time update received:', payload);
          refetch();
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
      });

    // Rotate between campaign and leaderboard with different durations
    const rotateDisplay = () => {
      setDisplayMode(current => {
        if (current === 'campaign') {
          // Schedule next rotation after leaderboard duration
          setTimeout(rotateDisplay, LEADERBOARD_DURATION);
          return 'leaderboard';
        } else {
          // Schedule next rotation after campaign duration
          setTimeout(rotateDisplay, CAMPAIGN_DURATION);
          return 'campaign';
        }
      });
    };

    // Start the rotation with campaign duration
    const initialTimeout = setTimeout(rotateDisplay, CAMPAIGN_DURATION);

    return () => {
      console.log('Cleaning up subscriptions...');
      supabase.removeChannel(channel);
      clearTimeout(initialTimeout);
    };
  }, [refetch]);

  if (isLoading) return <LoadingState />;
  
  if (error) {
    console.error('Error in ScreenDisplay:', error);
    return (
      <ErrorState 
        error="Failed to load tips. Please try again later." 
        onRetry={() => refetch()} 
      />
    );
  }

  if (!tips?.length) {
    return (
      <div className="min-h-screen">
        <div className="text-2xl text-white text-center">
          <p>No tips available yet.</p>
          <p className="text-lg mt-2 text-white/80">Be the first to share a tip!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-hidden">
      <div className="relative">
        {/* Background Elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-full opacity-30">
            <div className="absolute top-[10%] left-[5%] w-64 h-64 bg-vaseline-pink rounded-full filter blur-3xl animate-float" />
            <div className="absolute top-[30%] right-[10%] w-72 h-72 bg-vaseline-blue rounded-full filter blur-3xl animate-float2" />
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10">
          {displayMode === 'campaign' && <CampaignScreen />}
          {displayMode === 'leaderboard' && <ScoreBoard tips={tips} />}
        </div>
      </div>
    </div>
  );
}
